import {useFrame} from "@react-three/fiber";
import {GetRandomNumber} from "js-vextensions";
import React, {useMemo, useRef} from "react";
import * as THREE from "three";
import {LHQInstance} from "../../LHQ/Scene.js";

export const ChaoticSphereBoundsSet = ((props: {lhq: LHQInstance})=>{
	return <ChaoticSphereBounds/>;
});

interface ChaoticSphereBoundsProps {
  boundaryCenter?: [number, number, number];
  boundarySize?: [number, number, number]; // width, height, depth
  sphereCount?: number;
  bounceSpeed?: number;
  sphereRadius?: number;
}

export function ChaoticSphereBounds({
  // z-up: center/size order is (x, y-horizontal-extent, z-vertical-extent)
  boundaryCenter = [0, 0, 1.5],
  boundarySize = [4, 4, 3],
  sphereCount = 5,
  bounceSpeed = 2,
  sphereRadius = 0.1
}: ChaoticSphereBoundsProps) {
	const groupRef = useRef<THREE.Group>(null);

	// Sphere data
	const sphereRefs = useRef<THREE.Mesh[]>([]);
	const positions = useRef<THREE.Vector3[]>([]);
	const velocities = useRef<THREE.Vector3[]>([]);
	const nextBounceTime = useRef<number[]>([]);

	// Initialize spheres
	const sphereData = useMemo(()=>{
		const data = [] as {position: THREE.Vector3, velocity: THREE.Vector3}[];
		for (let i = 0; i < sphereCount; i++) {
			// Keep initial positions away from edges by sphere radius
			const maxXOffset = (boundarySize[0] / 2) - sphereRadius;
			const maxYOffset = (boundarySize[1] / 2) - sphereRadius;
			const maxZOffset = (boundarySize[2] / 2) - sphereRadius;

			const pos = new THREE.Vector3(
				GetRandomNumber({min: -maxXOffset, max: maxXOffset, randFunc: ()=>Math.random()}),
				GetRandomNumber({min: -maxYOffset, max: maxYOffset, randFunc: ()=>Math.random()}),
				GetRandomNumber({min: -maxZOffset, max: maxZOffset, randFunc: ()=>Math.random()}),
			);
			const vel = new THREE.Vector3(0, 0, 0);
			data.push({position: pos, velocity: vel});
		}
		return data;
	}, [sphereCount, boundarySize, sphereRadius]);

	// Initialize refs
	positions.current = sphereData.map(d=>d.position);
	velocities.current = sphereData.map(d=>d.velocity);
	nextBounceTime.current = sphereData.map(()=>Math.random() * 0.5 + 0.3);

	const bounceTimers = useRef<number[]>(new Array(sphereCount).fill(0));

	useFrame((state, delta)=>{
		for (let i = 0; i < sphereCount; i++) {
			bounceTimers.current[i] += delta;

			// Time for a new bounce?
			if (bounceTimers.current[i] >= nextBounceTime.current[i]) {
				bounceTimers.current[i] = 0;
				nextBounceTime.current[i] = Math.random() * 0.7 + 0.3;

				// Generate new random velocity
				const speed = bounceSpeed * (0.7 + Math.random() * 0.6);
				velocities.current[i].set(
					(Math.random() - 0.5) * speed,
					(Math.random() - 0.5) * speed,
					(Math.random() - 0.5) * speed
				);

				// 1 in 1000 chance of "breaking out" of boundary
				const breakout = Math.random() < 0.001;
				if (breakout) {
					velocities.current[i].multiplyScalar(3);
				}
			}

			// Move sphere
			positions.current[i].add(
				velocities.current[i].clone().multiplyScalar(delta)
			);

			// Boundary collision (accounting for sphere radius)
			const pos = positions.current[i];
			const vel = velocities.current[i];

			const boundaryMinX = 0 - boundarySize[0]/2 + sphereRadius;
			const boundaryMaxX = 0 + boundarySize[0]/2 - sphereRadius;
			const boundaryMinY = 0 - boundarySize[1]/2 + sphereRadius;
			const boundaryMaxY = 0 + boundarySize[1]/2 - sphereRadius;
			const boundaryMinZ = 0 - boundarySize[2]/2 + sphereRadius;
			const boundaryMaxZ = 0 + boundarySize[2]/2 - sphereRadius;

			if (pos.x < boundaryMinX || pos.x > boundaryMaxX) {
				vel.x *= -0.8;
				pos.x = pos.x < boundaryMinX ? boundaryMinX : boundaryMaxX;
			}
			if (pos.y < boundaryMinY || pos.y > boundaryMaxY) {
				vel.y *= -0.8;
				pos.y = pos.y < boundaryMinY ? boundaryMinY : boundaryMaxY;
			}
			if (pos.z < boundaryMinZ || pos.z > boundaryMaxZ) {
				vel.z *= -0.8;
				pos.z = pos.z < boundaryMinZ ? boundaryMinZ : boundaryMaxZ;
			}

			// Update sphere position
			if (sphereRefs.current[i]) {
				sphereRefs.current[i].position.copy(positions.current[i]);
			}
		}
	});

	return (
    <group ref={groupRef} position={boundaryCenter}>
      {/* Boundary wireframe */}
      <mesh>
        <boxGeometry args={boundarySize} />
        <meshBasicMaterial wireframe color="#888888" opacity={0.3} transparent />
      </mesh>

      {/* Spheres */}
      {Array.from({length: sphereCount}, (_, i)=>(
        <mesh
          key={i}
          ref={ref=>{
          	if (ref) sphereRefs.current[i] = ref;
          }}
        >
          <sphereGeometry args={[sphereRadius, 16, 16]} />
          <meshBasicMaterial color="#FF6B6B" />
        </mesh>
      ))}
    </group>
	);
}